import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  getDeviceDetailCountries,
  getDeviceDetailEmissions,
  getDeviceDetailDrivers,
  getDeviceDetailFuelTypes,
  getDeviceDetailNrmmStages,
  getDeviceDetailPowerUnits,
  getDeviceDetails,
  getDeviceInfo,
  updateDeviceDetails,
  updateVehicleDetails,
  type DeviceUpdatePayload,
  type VehicleUpdatePayload,
} from "src/services/deviceDetailsService";

export const deviceDetailsQueryKey = ["device-details"] as const;

export const useQueryDeviceDetails = (unitId?: string | number, enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...deviceDetailsQueryKey, unitId, language],
    queryFn: () => getDeviceDetails(unitId as string | number, language),
    select: (response) => response.data.response,
    enabled: enabled && !!unitId,
    retry: false,
  });
};

export const useQueryDeviceInfo = (unitId?: string | number, enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: ["device-info", unitId, language],
    queryFn: () => getDeviceInfo(unitId as string | number, language),
    select: (response) => response.data.response,
    enabled: enabled && !!unitId,
    retry: false,
  });
};

export const useMutationUpdateDeviceDetails = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: DeviceUpdatePayload }) =>
      updateDeviceDetails(id, payload, language),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceDetailsQueryKey }),
  });
};

export const useMutationUpdateVehicleDetails = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: VehicleUpdatePayload }) =>
      updateVehicleDetails(id, payload, language),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceDetailsQueryKey }),
  });
};

type LookupName = "powerUnits" | "emissions" | "nrmmStages" | "fuelTypes" | "countries";
const lookupFunctions = {
  powerUnits: getDeviceDetailPowerUnits,
  emissions: getDeviceDetailEmissions,
  nrmmStages: getDeviceDetailNrmmStages,
  fuelTypes: getDeviceDetailFuelTypes,
  countries: getDeviceDetailCountries,
} satisfies Record<LookupName, (language?: string) => Promise<{ data: { response: unknown[] } }>>;

export const useQueryDeviceDetailsLookup = (name: LookupName, enabled = true) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: ["device-details-lookup", name, language],
    queryFn: () => lookupFunctions[name](language),
    select: (response) => response.data.response,
    enabled,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
};

export const useQueryDeviceDetailDrivers = (customerId?: string | number) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);
  return useQuery({
    queryKey: ["device-details-drivers", customerId, language],
    queryFn: () => getDeviceDetailDrivers(customerId, language),
    select: (response) => response.data.response,
    enabled: !!customerId,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
};
